/**
 * React Query hooks for cumulative credit usage and team credit allocations.
 */

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { keyWithPrincipal } from "@/queries/principal-key";
import { Timestamp, toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import type { GetCumulativeCreditUsageResponse } from "gitpod-next-api/gitpod/v1/billing_pb";

export type PlainCumulativeCreditUsageResponse = PlainMessage<GetCumulativeCreditUsageResponse>;

const cumulativeUsageKeys = {
    /** Prefix key that matches all cumulative queries for an org (any asOf value). */
    all: (organizationId: string) => keyWithPrincipal(["cumulative-credit-usage", organizationId]),
    cumulative: (organizationId: string, asOfKey?: string) =>
        keyWithPrincipal(["cumulative-credit-usage", organizationId, asOfKey ?? "now"]),
};

/**
 * Options hook for cumulative credit usage.
 * @param asOf — compute cumulative usage up to this point in time. Omit for "up to now".
 */
export function useCumulativeCreditUsageOptions(organizationId: string, asOf?: Date) {
    const api = useGitpodAPI();
    const asOfKey = asOf ? asOf.toISOString() : undefined;

    return queryOptions({
        queryKey: cumulativeUsageKeys.cumulative(organizationId, asOfKey),
        queryFn: async () => {
            const response = await api.billingService.getCumulativeCreditUsage({
                organizationId,
                asOf: asOf ? Timestamp.fromDate(asOf) : undefined,
            });
            return toPlainMessage(response);
        },
        throwOnError: false,
        enabled: !!organizationId,
        staleTime: 60_000,
    });
}

/**
 * Fetch cumulative credit usage for an organization.
 * @param asOf — compute cumulative usage up to this point in time. Omit for "up to now".
 */
export function useCumulativeCreditUsage(organizationId: string, asOf?: Date) {
    return useQuery(useCumulativeCreditUsageOptions(organizationId, asOf));
}

export type UpdateTeamBudgetParams = {
    organizationId: string;
    teamId: string;
    creditBudget: number;
    /** true when the team has no existing allocation */
    isNew: boolean;
};

export function useUpdateTeamBudget() {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ organizationId, teamId, creditBudget, isNew }: UpdateTeamBudgetParams) => {
            if (!Number.isInteger(creditBudget) || creditBudget <= 0) {
                throw new Error("creditBudget must be a positive integer");
            }
            if (isNew) {
                const resp = await api.billingService.createTeamCreditAllocation({
                    organizationId,
                    teamId,
                    creditBudget: BigInt(creditBudget),
                });
                return resp.allocation ? toPlainMessage(resp.allocation) : undefined;
            }
            const resp = await api.billingService.updateTeamCreditAllocation({
                organizationId,
                teamId,
                creditBudget: BigInt(creditBudget),
            });
            return resp.allocation ? toPlainMessage(resp.allocation) : undefined;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey: cumulativeUsageKeys.all(organizationId),
            });
        },
    });
}

export function useDeleteTeamBudget() {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ organizationId, teamId }: { organizationId: string; teamId: string }) => {
            await api.billingService.deleteTeamCreditAllocation({
                organizationId,
                teamId,
            });
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey: cumulativeUsageKeys.all(organizationId),
            });
        },
    });
}
